import { useState } from 'react';
import { createZoomHistory, zoomHistoryReducer } from '../../components/GeneratedAxisChart/js/zoomHistory';
import type { ZoomHistory, ZoomViews } from '../../components/GeneratedAxisChart/js/zoomHistory';
import type { ZoomHandlerType } from '../../components/GeneratedAxisChart/hooks/useGeneratedAxisChart.types';

export interface UseZoomProps {
  /** Shared zoom history to display; false keeps the chart unzoomed and only reports selections to handleZoom. */
  zoom?: ZoomHistory | false;
  /** Receives every zoom action: 'zoom' with the new axis views, or 'reset', 'undo', 'redo', 'clear'. */
  handleZoom?: (data?: ZoomViews, type?: ZoomHandlerType) => void;
}

const updateZoomHistory = (current: ZoomHistory, data: ZoomViews | undefined, type: ZoomHandlerType): ZoomHistory => {
  if (type === 'zoom') return data ? zoomHistoryReducer(current, { type: 'commit', context: '', views: { ...current.present, ...data } }) : current;
  if (type === 'reset') return zoomHistoryReducer(current, { type: 'commit', context: '', views: {} });
  return zoomHistoryReducer(current, { type, context: '' });
};

/** Own zoom history unless zoom is false; a supplied history is displayed and handleZoom is always told. */
export const useZoom = (props: UseZoomProps = {}): { zoom: ZoomHistory; handleZoom: Required<UseZoomProps>['handleZoom'] } => {
  const [internal_zoom, setInternalZoom] = useState<ZoomHistory>(() => createZoomHistory(''));
  const zoom = props.zoom === false ? createZoomHistory('') : props.zoom ?? internal_zoom;
  const handleZoom: Required<UseZoomProps>['handleZoom'] = (data, type = 'zoom') => {
    if (props.zoom !== false) setInternalZoom((current) => updateZoomHistory(current, data, type));
    props.handleZoom?.(data, type);
  };
  return { zoom, handleZoom };
};
